import { useState, useCallback, useRef, useEffect } from 'react';
import { Show } from '../../types/Show';
import { debugLog, debugWarn } from '../../utils/debugLog';

export interface UseShowAnimationReturn {
  /** Current animation identifier (show-date combination) or null if no animation */
  animatingShow: string | null;
  
  /** Trigger animation for a specific show */
  triggerAnimation: (show: Show) => void;
  
  /** Clear current animation immediately */
  clearAnimation: () => void;
  
  /** Check if a specific show is currently animating */
  isAnimating: (show: Show) => boolean;
}

export interface UseShowAnimationOptions {
  /** Duration in milliseconds for the animation (default: 2000ms) */
  animationDuration?: number;
  
  /** Whether to log animation events for debugging (default: false) */
  enableLogging?: boolean;
}

/**
 * Custom hook for managing show animation state
 * 
 * Handles UI animation state for new show detection, providing:
 * - Animation state management with automatic cleanup
 * - Memoized performance optimization
 * - Configurable animation duration
 * - Debug logging support
 * 
 * @param options Configuration options for animation behavior
 * @returns Animation state and control functions
 * 
 * @example
 * ```tsx
 * const { animatingShow, triggerAnimation, isAnimating } = useShowAnimation({
 *   animationDuration: 3000,
 *   enableLogging: true
 * });
 * 
 * // Trigger animation for a show
 * triggerAnimation(newShow);
 * 
 * // Check if show is animating
 * const showIsAnimating = isAnimating(someShow);
 * ```
 */
export const useShowAnimation = (options: UseShowAnimationOptions = {}): UseShowAnimationReturn => {
  const {
    animationDuration = 2000,
    enableLogging = false
  } = options;

  // Animation state: stores the show identifier (show-date combination)
  const [animatingShow, setAnimatingShow] = useState<string | null>(null);
  
  // Timer ref for cleanup
  const animationTimer = useRef<NodeJS.Timeout | null>(null);

  // Generate unique identifier for a show
  const generateShowId = useCallback((show: Show): string => {
    return `${show.show}-${show.date_start_time}`;
  }, []);

  // Clear animation timer
  const clearAnimationTimer = useCallback(() => {
    if (animationTimer.current) {
      clearTimeout(animationTimer.current);
      animationTimer.current = null;
    }
  }, []);

  // Clear animation immediately
  const clearAnimation = useCallback(() => {
    if (enableLogging && animatingShow) {
      debugLog(`🎬 Animation cleared for show: ${animatingShow}`);
    }
    clearAnimationTimer();
    setAnimatingShow(null);
  }, [animatingShow, enableLogging, clearAnimationTimer]);

  // Trigger animation for a specific show
  const triggerAnimation = useCallback((show: Show) => {
    if (!show) {
      if (enableLogging) {
        debugWarn('🎬 triggerAnimation called with invalid show');
      }
      return;
    }

    const showId = generateShowId(show);
    
    if (enableLogging) {
      debugLog(`🎬 Triggering animation for show: ${show.show} (${showId})`);
    }

    // Clear any existing timer
    clearAnimationTimer();
    
    // Set the new animation
    setAnimatingShow(showId);
    
    // Set timer to clear animation after specified duration
    animationTimer.current = setTimeout(() => {
      if (enableLogging) {
        debugLog(`🎬 Animation completed for show: ${showId}`);
      }
      setAnimatingShow(null);
      animationTimer.current = null;
    }, animationDuration);
  }, [generateShowId, enableLogging, clearAnimationTimer, animationDuration]);

  // Check if a specific show is currently animating
  const isAnimating = useCallback((show: Show): boolean => {
    if (!show || !animatingShow) {
      return false;
    }
    return generateShowId(show) === animatingShow;
  }, [animatingShow, generateShowId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAnimationTimer();
    };
  }, [clearAnimationTimer]);

  return {
    animatingShow,
    triggerAnimation,
    clearAnimation,
    isAnimating,
  };
};
